import type { RuleDraft } from './accessRuleDraft';
import { kindSummaryLine, tenantSummaryLine, useResourceNames } from './accessRuleSentence';

interface AccessSummaryRoleOnlyProps {
  mode: 'role-only';
  tenantId: string;
  tenantName?: string;
  /** Whether tenantId is a partner (no parent tenant) — see
   * accessRuleSentence.ts's tenantSummaryLine/useIsPartnerTenant. */
  isPartner?: boolean;
  deploymentRules: RuleDraft[];
  workspaceRules: RuleDraft[];
}

interface AccessSummaryRoleAndUserProps {
  mode: 'role-and-user';
  tenantId: string;
  tenantName?: string;
  /** Whether tenantId is a partner (no parent tenant) — see
   * accessRuleSentence.ts's tenantSummaryLine/useIsPartnerTenant. */
  isPartner?: boolean;
  /** false: no role is assigned at all — the "Role allows" line is omitted
   * for both kinds, rather than shown as "Full access." (which would
   * wrongly imply an unrestricted role, not "no role"). */
  hasRole: boolean;
  roleDeploymentRules: RuleDraft[];
  roleWorkspaceRules: RuleDraft[];
  userDeploymentRules: RuleDraft[];
  userWorkspaceRules: RuleDraft[];
}

export type AccessSummaryProps = AccessSummaryRoleOnlyProps | AccessSummaryRoleAndUserProps;

function KindEffectiveAccess({
  label,
  hasRole,
  roleLine,
  userLine,
}: {
  label: string;
  hasRole: boolean;
  roleLine: string;
  userLine: string;
}) {
  const eitherRestricts = (hasRole && roleLine !== 'Full access.') || userLine !== 'Full access.';
  return (
    <div className="space-y-1">
      <dt className="font-medium text-foreground">{label}</dt>
      <dd className="space-y-0.5 text-muted-foreground">
        {hasRole && (
          <div className="flex gap-2">
            <span className="shrink-0">Role allows:</span>
            <span>{roleLine}</span>
          </div>
        )}
        <div className="flex gap-2">
          <span className="shrink-0">Their access:</span>
          <span>{userLine}</span>
        </div>
        {eitherRestricts && (
          <p className="text-xs italic">→ both must match for this person to reach it</p>
        )}
      </dd>
    </div>
  );
}

/**
 * Categorized access summary (SUN-408 round 3), in one of two modes:
 * - 'role-only': Tenants / Deployments / Workspaces, one line each — for a
 *   role's own rules alone (no "user" side exists at that layer). Used on
 *   the role wizard's Access step (via RoleAccessRulesEditor) and Confirm
 *   step, and RoleDetailPage's Access tab.
 * - 'role-and-user': the same three lines, but Deployments/Workspaces each
 *   show the role's line and the person's own line side by side — a
 *   person's effective access is the AND of what their role allows and what
 *   their own access_rules further narrow, never wider than the role
 *   (accessrule.gateBoth). Used on the user wizard's Access and Confirm
 *   steps, and UserDetailPage's Access rules tab.
 *
 * Consolidates what were three separate implementations (AccessRuleSummaryList,
 * EffectiveAccessSummary, and a third inline copy in the role wizard's
 * Confirm step) into one component reused everywhere.
 */
export function AccessSummary(props: AccessSummaryProps) {
  const resourceName = useResourceNames(props.tenantId);

  if (props.mode === 'role-only') {
    const { tenantId, tenantName, isPartner, deploymentRules, workspaceRules } = props;
    return (
      <div className="space-y-1.5">
        <dl className="space-y-1.5 text-sm">
          <div className="flex gap-2">
            <dt className="font-medium text-foreground shrink-0">Tenants —</dt>
            <dd className="text-muted-foreground">
              {tenantSummaryLine(tenantId, tenantName, isPartner)}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-medium text-foreground shrink-0">Deployments —</dt>
            <dd className="text-muted-foreground">
              {kindSummaryLine(deploymentRules, resourceName)}
            </dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-medium text-foreground shrink-0">Workspaces —</dt>
            <dd className="text-muted-foreground">
              {kindSummaryLine(workspaceRules, resourceName)}
            </dd>
          </div>
        </dl>
        <p className="text-xs text-muted-foreground">
          A workspace is reachable only if it passes both the Deployments rule and the Workspaces
          rule above.
        </p>
      </div>
    );
  }

  const {
    tenantId,
    tenantName,
    isPartner,
    hasRole,
    roleDeploymentRules,
    roleWorkspaceRules,
    userDeploymentRules,
    userWorkspaceRules,
  } = props;
  return (
    <div className="space-y-3">
      <dl className="space-y-3 text-sm">
        <div className="flex gap-2">
          <dt className="font-medium text-foreground shrink-0">Tenants —</dt>
          <dd className="text-muted-foreground">
            {tenantSummaryLine(tenantId, tenantName, isPartner)}
          </dd>
        </div>
        <KindEffectiveAccess
          label="Deployments"
          hasRole={hasRole}
          roleLine={kindSummaryLine(roleDeploymentRules, resourceName)}
          userLine={kindSummaryLine(userDeploymentRules, resourceName)}
        />
        <KindEffectiveAccess
          label="Workspaces"
          hasRole={hasRole}
          roleLine={kindSummaryLine(roleWorkspaceRules, resourceName)}
          userLine={kindSummaryLine(userWorkspaceRules, resourceName)}
        />
      </dl>
      <p className="text-xs text-muted-foreground">
        A workspace is reachable only if it passes both the Deployments rule and the Workspaces rule
        above.
      </p>
    </div>
  );
}
